import { ctfBases, hillAt, ticksToNextZone, trophyHome, zoneInset, zoneRing, type GameEngine } from './engine.js';
import { DX, DY, FOOD_TROPHY, isOpposite, type Dir, type PlayerState } from './types.js';

export type BotDifficulty = 'easy' | 'medium' | 'hard';

const LOOKAHEAD: Record<BotDifficulty, number> = { easy: 20, medium: 60, hard: 140 };
const MISTAKE_RATE: Record<BotDifficulty, number> = { easy: 0.08, medium: 0.025, hard: 0.005 };

/**
 * Bot controller: for each legal heading, score = reachable free area
 * (bounded flood fill, avoids trapping itself) + attraction to the nearest food.
 *
 * Everything that is the same for every bot on a tick (the board of bodies, obstacles and the zone
 * fence, the food list, the trophy) is worked out once per tick, then each bot only patches the few
 * cells where its view differs: the squares round its own head, which it doesn't fear. That keeps a
 * full arena's bots cheap on a phone, and allocation-free, so they don't cause GC pauses in the game
 * loop or on the server. Decisions are exactly those of one board per bot (replays depend on it).
 */
interface BotTick {
  engine: GameEngine;
  width: number;
  height: number;
  /** Bodies (1), squares next to any head (2), then obstacles and the zone fence (1), in that order. */
  board: Uint8Array;
  /** 1 where an obstacle or the zone fence is: those win over everything else. */
  fixed: Uint8Array;
  /** Index of the last player whose body covers each cell, -1 for none. */
  bodyLast: Int16Array;
  food: Int32Array;
  foodCount: number;
  current: number;
  /** Food this close to the edge is ignored (battle royale), 0 for none. */
  margin: number;
  /** Capture the food: the trophy's cell while it lies on the board. */
  trophy: number | null;
}

let ctx: BotTick | null = null;

/**
 * The shared view for one round of decisions, rebuilt on every call (the engine may have changed
 * since, even within a tick) but into the same buffers, so it allocates nothing once warmed up.
 */
function botTick(engine: GameEngine): BotTick {
  const { width, height } = engine.config;
  const size = width * height;
  const c: BotTick =
    ctx && ctx.board.length >= size
      ? ctx
      : {
          engine,
          width,
          height,
          board: new Uint8Array(size),
          fixed: new Uint8Array(size),
          bodyLast: new Int16Array(size),
          food: new Int32Array(Math.max(64, engine.food.size * 2)),
          foodCount: 0,
          current: 0,
          margin: 0,
          trophy: null,
        };
  c.engine = engine;
  c.width = width;
  c.height = height;
  const board = c.board.subarray(0, size);
  const fixed = c.fixed.subarray(0, size);
  const bodyLast = c.bodyLast.subarray(0, size);
  board.fill(0);
  fixed.fill(0);
  bodyLast.fill(-1);
  const players = engine.players;
  for (let i = 0; i < players.length; i++) {
    const p = players[i]!;
    if (!p.alive) continue;
    // Tails will usually move away this tick, so they are not treated as walls.
    const len = p.grow > 0 ? p.body.length : p.body.length - 1;
    for (let k = 0; k < len; k++) {
      const cell = p.body[k]!;
      board[cell] = 1;
      bodyLast[cell] = i;
    }
    // Squares next to an enemy head are dangerous (possible head-on). Every head is marked here;
    // each bot puts back the squares round its own head (see ownView).
    const h = p.body[0]!;
    const hx = h % width;
    const hy = (h - hx) / width;
    for (let d = 0; d < 4; d++) {
      const x = hx + DX[d]!;
      const y = hy + DY[d]!;
      if (x >= 0 && y >= 0 && x < width && y < height) board[y * width + x] = 2;
    }
  }
  for (const cell of engine.obstacles) {
    board[cell] = 1;
    fixed[cell] = 1;
  }
  // Zone: the rings just outside it are walls, and so are the rings the next step will close. A fence
  // two rings deep keeps a bot in and is much cheaper than marking every outside cell of a big arena.
  const soon = ticksToNextZone(engine.config, engine.tick);
  const current = zoneInset(engine.config, engine.tick);
  // Always keep clear of the next step's rings: a long snake needs time to get out, and a big
  // arena's zone closes several rings at a time.
  const inset = current + (soon !== null ? zoneRing(engine.config) : 0);
  for (let r = Math.max(0, current - 1); r < inset; r++) {
    for (let x = r; x < width - r; x++) {
      board[r * width + x] = fixed[r * width + x] = 1;
      board[(height - 1 - r) * width + x] = fixed[(height - 1 - r) * width + x] = 1;
    }
    for (let y = r; y < height - r; y++) {
      board[y * width + r] = fixed[y * width + r] = 1;
      board[y * width + width - 1 - r] = fixed[y * width + width - 1 - r] = 1;
    }
  }
  c.current = current;
  ctx = c;
  // Battle royale: ignore food the next couple of zone steps will swallow. Chasing it is how a
  // long snake ends up pinned in a corner of the closing zone.
  c.margin = engine.config.rules?.shrink ? current + 2 * zoneRing(engine.config) + 2 : 0;
  // Food cells in the map's own order (the nearest-food search keeps the first of equals).
  if (c.food.length < engine.food.size) c.food = new Int32Array(engine.food.size * 2);
  c.foodCount = 0;
  c.trophy = null;
  engine.food.forEach(collectFood);
  return c;
}

/** Fills the current per-tick view's food list (a named function: no closure per call). */
function collectFood(f: { kind: number }, cell: number): void {
  const c = ctx!;
  c.food[c.foodCount++] = cell;
  if (c.trophy === null && f.kind === FOOD_TROPHY) c.trophy = cell;
}

/** Undo list for ownView: up to four cells and the values to put back. */
const patched = new Int32Array(4);
const patchedWas = new Uint8Array(4);

/**
 * The board as this bot sees it: the squares round its own head hold what the other players
 * (and walls) put there, not its own head's danger mark. Returns how many cells it changed.
 */
function ownView(c: BotTick, me: PlayerState, meIndex: number): number {
  const { width, height, board, fixed, bodyLast, engine } = c;
  const h = me.body[0]!;
  const hx = h % width;
  const hy = (h - hx) / width;
  let n = 0;
  for (let d = 0; d < 4; d++) {
    const x = hx + DX[d]!;
    const y = hy + DY[d]!;
    if (x < 0 || y < 0 || x >= width || y >= height) continue;
    const cell = y * width + x;
    let value: number;
    if (fixed[cell]) value = 1;
    else {
      // The last write wins, players in order, each one's body before its head marks.
      let lastHead = -1;
      const players = engine.players;
      for (let i = 0; i < players.length; i++) {
        if (i === meIndex) continue;
        const p = players[i]!;
        if (!p.alive) continue;
        const q = p.body[0]!;
        const qx = q % width;
        const qy = (q - qx) / width;
        if (Math.abs(qx - x) + Math.abs(qy - y) === 1) lastHead = i;
      }
      const body = bodyLast[cell]!;
      value = lastHead >= 0 && lastHead >= body ? 2 : body >= 0 ? 1 : 0;
    }
    if (board[cell] !== value) {
      patched[n] = cell;
      patchedWas[n] = board[cell]!;
      board[cell] = value;
      n++;
    }
  }
  return n;
}

function restore(c: BotTick, n: number): void {
  for (let i = 0; i < n; i++) c.board[patched[i]!] = patchedWas[i]!;
}

export function chooseBotDirection(
  engine: GameEngine,
  slot: number,
  difficulty: BotDifficulty = 'medium',
  random: () => number = Math.random,
): Dir | null {
  const me = engine.players[slot];
  if (!me || !me.alive || engine.phase !== 'running') return null;
  const c = botTick(engine);
  const changed = ownView(c, me, engine.players.indexOf(me));
  try {
    return decide(c, me, difficulty, random);
  } finally {
    restore(c, changed);
  }
}

function decide(c: BotTick, me: PlayerState, difficulty: BotDifficulty, random: () => number): Dir | null {
  const { engine, width, height, board: blocked, margin } = c;
  const head = me.body[0]!;
  const hx = head % width;
  const hy = Math.floor(head / width);
  const limit = Math.max(LOOKAHEAD[difficulty], me.body.length * 2);

  let nearestFood: number | null = null;
  let best = Infinity;
  const food = c.food;
  for (let i = 0; i < c.foodCount; i++) {
    const cell = food[i]!;
    const cx = cell % width;
    const cy = Math.floor(cell / width);
    if (margin && (cx < margin || cy < margin || cx >= width - margin || cy >= height - margin)) continue;
    const d = Math.abs(cx - hx) + Math.abs(cy - hy);
    if (d < best) {
      best = d;
      nearestFood = cell;
    }
  }
  // Objective modes: head for the hill / trophy / own base unless food is right there.
  const goal = objectiveCell(engine, me, c.trophy);
  if (goal !== null && best > 3) nearestFood = goal;

  let bestDir: Dir | null = null;
  let bestScore = -Infinity;
  for (let d = 0 as Dir; d < 4; d = (d + 1) as Dir) {
    if (isOpposite(d, me.dir)) continue;
    const x = hx + DX[d];
    const y = hy + DY[d];
    if (x < 0 || y < 0 || x >= width || y >= height) continue;
    const cell = y * width + x;
    if (blocked[cell] === 1) continue;
    const area = floodFill(blocked, width, height, cell, limit);
    let score = Math.min(area, limit) * 10;
    if (area < me.body.length) score -= 2000;
    if (blocked[cell] === 2) score -= 600;
    if (nearestFood !== null) {
      const fx = nearestFood % width;
      const fy = Math.floor(nearestFood / width);
      score -= (Math.abs(fx - x) + Math.abs(fy - y)) * 6;
    }
    if (d === me.dir) score += 2;
    if (random() < MISTAKE_RATE[difficulty]) score += (random() - 0.5) * 800;
    if (score > bestScore) {
      bestScore = score;
      bestDir = d;
    }
  }
  return bestDir;
}

/** King of the hill: the hill. Capture the food: own base when carrying, else the trophy. */
function objectiveCell(engine: GameEngine, me: PlayerState, trophy: number | null): number | null {
  const { config } = engine;
  const w = config.width;
  const hill = hillAt(config, engine.tick);
  if (hill) return (hill.y + Math.floor(hill.h / 2)) * w + hill.x + Math.floor(hill.w / 2);
  if (!config.rules?.ctf) return null;
  if (me.carrying) {
    const base = ctfBases(config)[me.team];
    return base ? (base.y + Math.floor(base.h / 2)) * w + base.x + Math.floor(base.w / 2) : null;
  }
  if (trophy !== null) return trophy;
  // Someone has it: go back toward the middle where it reappears.
  return trophyHome(config);
}

// Reused scratch buffers: no allocation per call, which keeps GC pauses off the game loop.
let stamp = new Uint32Array(0);
let stack = new Int32Array(0);
let epoch = 0;

function floodFill(blocked: Uint8Array, width: number, height: number, start: number, limit: number): number {
  const size = width * height;
  if (stamp.length < size) {
    stamp = new Uint32Array(size);
    stack = new Int32Array(size);
    epoch = 0;
  }
  epoch++;
  if (epoch === 0xffffffff) {
    stamp.fill(0);
    epoch = 1;
  }
  let sp = 0;
  stack[sp++] = start;
  stamp[start] = epoch;
  let count = 0;
  while (sp > 0 && count < limit) {
    const c = stack[--sp]!;
    count++;
    const x = c % width;
    if (x > 0 && blocked[c - 1] !== 1 && stamp[c - 1] !== epoch) { stamp[c - 1] = epoch; stack[sp++] = c - 1; }
    if (x < width - 1 && blocked[c + 1] !== 1 && stamp[c + 1] !== epoch) { stamp[c + 1] = epoch; stack[sp++] = c + 1; }
    if (c >= width && blocked[c - width] !== 1 && stamp[c - width] !== epoch) { stamp[c - width] = epoch; stack[sp++] = c - width; }
    if (c + width < size && blocked[c + width] !== 1 && stamp[c + width] !== epoch) { stamp[c + width] = epoch; stack[sp++] = c + width; }
  }
  return count;
}

/**
 * Every bot's choice for the coming tick, by slot (null for humans, the dead, or no move). Bots are
 * decided in slot order with one shared random source, exactly as driveBots always has.
 */
export function decideBots(
  engine: GameEngine,
  difficulty: BotDifficulty = 'medium',
  random: () => number = Math.random,
): Array<Dir | null> {
  const out: Array<Dir | null> = new Array(engine.players.length).fill(null);
  if (engine.phase !== 'running') return out;
  let c: BotTick | null = null;
  const players = engine.players;
  for (let i = 0; i < players.length; i++) {
    const p = players[i]!;
    if (!p.isBot || !p.alive) continue;
    c ??= botTick(engine);
    const changed = ownView(c, p, i);
    out[p.slot] = decide(c, p, difficulty, random);
    restore(c, changed);
  }
  return out;
}

/** Convenience: let every bot in the engine queue its turn for the next tick. */
export function driveBots(
  engine: GameEngine,
  difficulty: BotDifficulty = 'medium',
  random: () => number = Math.random,
): void {
  if (engine.phase !== 'running') return;
  let c: BotTick | null = null;
  const players = engine.players;
  for (let i = 0; i < players.length; i++) {
    const p = players[i]!;
    if (!p.isBot || !p.alive) continue;
    c ??= botTick(engine);
    const changed = ownView(c, p, i);
    const dir = decide(c, p, difficulty, random);
    restore(c, changed);
    if (dir !== null && dir !== p.dir) engine.queueInput(p.slot, dir);
  }
}
